import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { ContextType } from '@lit/context';
import { SlSwitch } from '@shoelace-style/shoelace';
import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { api } from './api.js';
import { docLinkStyles, pageStyles } from './components/cssstyles.js';
import { OrganizationContext } from './components/organization-context.js';
import { consume, contexts, FKOrgChanged } from './contexts.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

@customElement('page-organization-billing')
export class PageOrganizationBilling extends LitElement {

  static url(options: { orgSlug: string }): string {
    return platformUrl(['', options.orgSlug, 'billing'].join('/'));
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/billing',
      ],
      render: (groups) => OrganizationContext.render({
        orgSlug: groups?.org,
      }, html`
        <page-organization-billing
          .orgSlug=${groups?.org}
        ></page-organization-billing>
      `),
    }];
  }

  @consume({ context: contexts.org, subscribe: true }) private _org?: ContextType<typeof contexts.org>;
  @consume({ context: contexts.user, subscribe: true }) private _user?: ContextType<typeof contexts.user>;
  @property({ attribute: false }) orgSlug?: string;

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug] as const,
    metadata: ([orgSlug]) => orgSlug ? pageMetadata({ orgSlug, section: 'Billing', accessGated: true }) : undefined,
  });

  render() {
    if (this._org && this._org.access !== 'admin' && this._org.access !== 'owner')
      return html`<page-http-error code=403></page-http-error>`;

    return html`
      <org-header .orgSlug=${this.orgSlug} submenu=billing></org-header>
      <app-body wide user-role=${this._org?.access}>
        <h1>Billing & Subscription</h1>
        ${this._user?.isSuperUser ? html`
          <fk-callout variant=tip>
            <sl-icon slot=icon name=stars></sl-icon>
            <sl-switch
              ?checked=${!!this._org?.premiumFreePlan}
              @sl-change=${async (event: Event) => {
                const checked = (event.target as SlSwitch).checked;
                if (!this.orgSlug)
                  return;
                await api.administration.setOrgPremiumFreePlan.POST({
                  orgSlug: this.orgSlug,
                  premiumFreePlan: checked,
                });
                this.dispatchEvent(new FKOrgChanged({ orgSlug: this.orgSlug }));
              }}
            >Grant the Open Source plan to this organization</sl-switch>
          </fk-callout>
        ` : nothing}
        <product-plans
          .orgSlug=${this.orgSlug}
        ></product-plans>
      </app-body>
      <app-footer></app-footer>
    `
  }

  static styles = [githubMarkdownCSS, docLinkStyles, pageStyles, css`

  `];
}
